import { useState, useEffect } from 'react';
import apiClient from '../services/api';
import { useToast } from './use-toast';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

export const usePushNotifications = () => {
  const { toast } = useToast();
  const [supported] = useState(
    () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(!!sub))
      .catch(() => {});
  }, [supported]);

  const subscribe = async () => {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      toast({ title: 'Notifications refusées', description: 'Autorisez les notifications dans les paramètres du navigateur', variant: 'destructive' });
      return;
    }
    const { data } = await apiClient.get('/push/vapid-public-key');
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
    await apiClient.post('/push/subscribe', { subscription: sub.toJSON() });
    setSubscribed(true);
    toast({ title: 'Notifications activées', description: 'Vous recevrez les alertes de gardes et rondes sur cet appareil' });
  };

  const unsubscribe = async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await apiClient.post('/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {});
      await sub.unsubscribe();
    }
    setSubscribed(false);
    toast({ title: 'Notifications désactivées' });
  };

  const toggle = async () => {
    if (!supported || busy) return;
    setBusy(true);
    try {
      if (subscribed) {
        await unsubscribe();
      } else {
        await subscribe();
      }
    } catch (e) {
      toast({ title: 'Erreur notifications', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return { supported, subscribed, busy, toggle };
};
